import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  SwapHoriz,
  ContentCopy,
  OpenInNew
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const TransactionHistory = ({ walletAddress, transactions = [] }) => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filter, setFilter] = useState('all'); // 'all', 'sent', 'received', 'swapped'

  useEffect(() => {
    // Set loading to false when transactions are passed in
    setLoading(false);
  }, [transactions]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#00ff00';
      case 'pending':
        return '#ffaa00';
      case 'failed':
        return '#ff0000';
      default:
        return '#ffffff';
    }
  };

  const getTransactionTypeIcon = (type, isReceived = false) => {
    switch (type) {
      case 'send':
        return isReceived ? <ArrowDownward sx={{ color: '#00ff00' }} /> : <ArrowUpward sx={{ color: '#ff5555' }} />;
      case 'swap':
        return <SwapHoriz sx={{ color: '#00a3ff' }} />;
      default:
        return isReceived ? <ArrowDownward sx={{ color: '#00ff00' }} /> : <ArrowUpward sx={{ color: '#ff5555' }} />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const filteredTransactions = transactions.filter(tx => {
    if (filter === 'all') return true;
    if (filter === 'sent') return tx.type === 'send';
    if (filter === 'received') return tx.type === 'receive';
    if (filter === 'swapped') return tx.type === 'swap';
    return true;
  });

  const displayedTransactions = filteredTransactions
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'rgba(0, 30, 60, 0.5)',
        p: 3,
        borderRadius: 2,
        border: '1px solid rgba(0, 163, 255, 0.2)',
        mt: 3
      }}
    >
      <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
        Transaction History
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          label="All"
          onClick={() => setFilter('all')}
          sx={{
            bgcolor: filter === 'all' ? 'rgba(0, 163, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0, 163, 255, 0.2)' }
          }}
        />
        <Chip
          label="Sent"
          onClick={() => setFilter('sent')}
          sx={{
            bgcolor: filter === 'sent' ? 'rgba(0, 163, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0, 163, 255, 0.2)' }
          }}
        />
        <Chip
          label="Received"
          onClick={() => setFilter('received')}
          sx={{
            bgcolor: filter === 'received' ? 'rgba(0, 163, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0, 163, 255, 0.2)' }
          }}
        />
        <Chip
          label="Swapped"
          onClick={() => setFilter('swapped')}
          sx={{
            bgcolor: filter === 'swapped' ? 'rgba(0, 163, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0, 163, 255, 0.2)' }
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress sx={{ color: '#00a3ff' }} />
        </Box>
      ) : transactions.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            No transactions found
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: 'rgba(0, 30, 60, 0.8)', color: 'white' }}>Type</TableCell>
                  <TableCell sx={{ bgcolor: 'rgba(0, 30, 60, 0.8)', color: 'white' }}>Date</TableCell>
                  <TableCell sx={{ bgcolor: 'rgba(0, 30, 60, 0.8)', color: 'white' }}>Amount</TableCell>
                  <TableCell sx={{ bgcolor: 'rgba(0, 30, 60, 0.8)', color: 'white' }}>Address</TableCell>
                  <TableCell sx={{ bgcolor: 'rgba(0, 30, 60, 0.8)', color: 'white' }}>Status</TableCell>
                  <TableCell sx={{ bgcolor: 'rgba(0, 30, 60, 0.8)', color: 'white' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedTransactions.map((tx, index) => {
                  const isReceived = tx.type === 'receive';

                  return (
                    <motion.tr
                      key={tx.hash || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      component={TableRow}
                      sx={{
                        '&:nth-of-type(odd)': { bgcolor: 'rgba(0, 30, 60, 0.3)' },
                        '&:nth-of-type(even)': { bgcolor: 'rgba(0, 30, 60, 0.5)' },
                        '&:hover': { bgcolor: 'rgba(0, 163, 255, 0.1)' }
                      }}
                    >
                      <TableCell sx={{ color: 'white' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getTransactionTypeIcon(tx.type, isReceived)}
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {tx.type === 'receive' ? 'Received' : tx.type === 'swap' ? 'Swapped' : 'Sent'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        {formatDate(tx.timestamp)}
                      </TableCell>
                      <TableCell sx={{ color: isReceived ? '#00ff00' : tx.type === 'swap' ? '#00a3ff' : '#ff5555' }}>
                        {tx.type === 'swap' ? (
                          <span>{tx.fromAmount} {tx.fromToken} → {tx.toAmount} {tx.toToken}</span>
                        ) : (
                          <span>{isReceived ? '+' : '-'}{tx.amount} {tx.token}</span>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'white' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {tx.type === 'receive' ? tx.from : tx.type === 'send' ? tx.to : 'Swap'}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyToClipboard(tx.type === 'receive' ? tx.from : tx.type === 'send' ? tx.to : '')}
                            sx={{ color: 'rgba(255,255,255,0.7)', ml: 1 }}
                            disabled={tx.type === 'swap'}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={tx.status}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(0, 0, 0, 0.3)',
                            color: getStatusColor(tx.status),
                            borderColor: getStatusColor(tx.status),
                            borderWidth: 1,
                            borderStyle: 'solid'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {tx.hash && (
                          <Tooltip title="View on Explorer">
                            <IconButton
                              size="small"
                              sx={{ color: '#00a3ff' }}
                              onClick={() => {
                                const url = tx.type === 'swap'
                                  ? `https://etherscan.io/tx/${tx.hash}`
                                  : `https://sepolia.etherscan.io/tx/${tx.hash}`;
                                window.open(url, '_blank');
                              }}
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </motion.tr>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredTransactions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              color: 'white',
              '.MuiTablePagination-selectIcon': { color: 'white' },
              '.MuiTablePagination-select': { color: 'white' },
              '.MuiTablePagination-selectLabel': { color: 'white' },
              '.MuiTablePagination-displayedRows': { color: 'white' },
              '.MuiTablePagination-actions': { color: 'white' }
            }}
          />
        </>
      )}
    </Paper>
  );
};

export default TransactionHistory;
